/**
 * Utility types for declaring keyOf T where you may need to reference those keys as
 *  camelCase, kebab-case, snake_case, or SCREAMING_SNAKE_CASE
 *
 * Directory, for instance, needs to put "FULL_NAME" in graphQL but easily understand that
 *  FULL_NAME is really a keyof Person (fullName)
 */

export type Upper =
  | "A"
  | "B"
  | "C"
  | "D"
  | "E"
  | "F"
  | "G"
  | "H"
  | "I"
  | "J"
  | "K"
  | "L"
  | "M"
  | "N"
  | "O"
  | "P"
  | "Q"
  | "R"
  | "S"
  | "T"
  | "U"
  | "V"
  | "W"
  | "X"
  | "Y"
  | "Z";

export type Lower =
  | "a"
  | "b"
  | "c"
  | "d"
  | "e"
  | "f"
  | "g"
  | "h"
  | "i"
  | "j"
  | "k"
  | "l"
  | "m"
  | "n"
  | "o"
  | "p"
  | "q"
  | "r"
  | "s"
  | "t"
  | "u"
  | "v"
  | "w"
  | "x"
  | "y"
  | "z";

export type KebabCase<T extends string | number | symbol> =
  T extends `${infer L}${Upper}${infer R}`
    ? T extends `${L}${infer U}${R}`
      ? `${L}-${Lowercase<U>}${KebabCase<R>}`
      : T
    : T;

type SnakeCaseRecurse<T extends string> =
  T extends `${infer First}${infer Rest}`
    ? First extends Uppercase<First> | `${number}`
      ? `_${Lowercase<First>}${SnakeCaseRecurse<Rest>}`
      : `${First}${SnakeCaseRecurse<Rest>}`
    : T;
export type SnakeCase<T extends string> =
  T extends `${infer First}${infer Rest}`
    ? `${Lowercase<First>}${SnakeCaseRecurse<Rest>}`
    : T;

export type ScreamingSnakeCase<T extends string> =
  T extends `${infer L}${Upper}${infer R}`
    ? T extends `${L}${infer U}${R}`
      ? `${Uppercase<L>}_${Uppercase<U>}${Uppercase<ScreamingSnakeCase<R>>}`
      : T
    : T extends `${infer L}`
    ? `${Uppercase<L>}`
    : T;

export type CamelCaseFromSnakeCase<T extends string | number | symbol> =
  T extends `${infer A}_${infer B}${infer R}`
    ? CamelCaseFromSnakeCase<`${A}${Capitalize<B>}${R}`>
    : T extends string
    ? `${Uncapitalize<T>}`
    : never;

export type CamelCaseFromKebabCase<T extends string | number | symbol> =
  T extends `${infer A}-${infer B}`
    ? CamelCaseFromKebabCase<`${A}${Capitalize<B>}`>
    : T;

export type LowercaseKey<T extends string | number | symbol> = T extends string
  ? `${Lowercase<T>}`
  : T;

export type KeyOfType<T, V> = keyof {
  [K in keyof T as T[K] extends V ? K : never]: V;
};
